import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import { type GoalOperator } from '@/types'

interface DataPoint {
  date: string
  value: number | null
  timestamp: number
  isForecast?: boolean
  count?: number // Number of readings averaged for this period
}

interface MetricChartProps {
  data: DataPoint[]
  unit?: string | null
  targetValue?: number
  targetMin?: number
  targetMax?: number
  operator?: GoalOperator
}

// Format value for display (handles small decimals cleanly)
function formatValue(value: number, unit: string | null | undefined): string {
  // Handle small decimals - show up to 2 decimal places
  const formatted = Math.abs(value) < 10
    ? value.toFixed(2).replace(/\.?0+$/, '') // Remove trailing zeros
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 })

  if (unit === '$') return `$${formatted}`
  if (unit === '%') return `${formatted}%`
  return formatted
}

// Data point with guaranteed non-null value (for calculations)
interface ActualDataPoint extends Omit<DataPoint, 'value'> {
  value: number
}

// Calculate forecast value by extrapolating recent trend (last 2 points)
function calculateForecastValue(data: ActualDataPoint[]): number | null {
  if (data.length < 2) return null

  // Use last 2 points to capture immediate trend direction
  const last = data[data.length - 1].value
  const prev = data[data.length - 2].value
  const trend = last - prev

  // Extrapolate: continue the trend
  let forecastValue = last + trend

  // Clamp to reasonable bounds (within 50% of last value, floor at 0)
  const lowerBound = Math.max(0, last * 0.5)
  const upperBound = last * 1.5
  forecastValue = Math.max(lowerBound, Math.min(upperBound, forecastValue))

  return Math.round(forecastValue * 100) / 100
}

// Custom tooltip component
function CustomTooltip({ active, payload, label, unit, operator, targetValue, targetMin, targetMax }: {
  active?: boolean
  payload?: { value: number; dataKey: string; payload?: DataPoint }[]
  label?: string
  unit?: string | null
  operator?: GoalOperator
  targetValue?: number
  targetMin?: number
  targetMax?: number
}) {
  if (!active || !payload?.length) return null

  const dataPoint = payload[0]?.payload
  const value = dataPoint?.value
  const isForecast = dataPoint?.isForecast

  // Don't show tooltip for null/undefined values
  if (value === undefined || value === null) return null

  // Calculate difference from target
  let diff: number | null = null
  let targetDisplay: string | null = null
  let isMet: boolean | null = null

  if (operator && !isForecast) {
    if (operator === 'range' && targetMin !== undefined && targetMax !== undefined) {
      targetDisplay = `${formatValue(targetMin, unit)} – ${formatValue(targetMax, unit)}`
      if (value < targetMin) {
        diff = value - targetMin
        isMet = false
      } else if (value > targetMax) {
        diff = value - targetMax
        isMet = false
      } else {
        isMet = true
      }
    } else if (targetValue !== undefined) {
      targetDisplay = formatValue(targetValue, unit)
      diff = value - targetValue
      if (operator === 'gte') {
        isMet = value >= targetValue
      } else if (operator === 'lte') {
        isMet = value <= targetValue
      }
    }
  }

  const count = dataPoint?.count

  return (
    <div className="bg-white px-3 py-2 shadow-lg rounded-lg border border-gray-200 min-w-[140px]">
      <p className="text-xs text-gray-500 mb-1">
        {isForecast ? `${label} (Forecast)` : label}
      </p>
      <p className={`text-sm font-semibold ${isForecast ? 'text-gray-500' : 'text-gray-900'}`}>
        {formatValue(value, unit)}
      </p>
      {count && count > 1 && !isForecast && (
        <p className="text-xs text-gray-400">{count} readings averaged</p>
      )}
      {targetDisplay && !isForecast && (
        <>
          <p className="text-xs text-gray-400 mt-1">
            Target: {operator === 'gte' ? '≥ ' : operator === 'lte' ? '≤ ' : ''}{targetDisplay}
          </p>
          {diff !== null && (
            <p className={`text-xs font-medium mt-0.5 ${isMet ? 'text-success-600' : 'text-danger-600'}`}>
              {diff > 0 ? '+' : ''}{formatValue(diff, unit)} {diff > 0 ? 'above' : diff < 0 ? 'below' : 'at'} target
            </p>
          )}
          {diff === null && isMet && (
            <p className="text-xs font-medium mt-0.5 text-success-600">Within range</p>
          )}
        </>
      )}
    </div>
  )
}

// Helper to determine if a value meets the target
function isValueMet(
  value: number,
  operator: GoalOperator | undefined,
  targetValue: number | undefined,
  targetMin: number | undefined,
  targetMax: number | undefined
): boolean | null {
  if (targetValue === undefined || operator === undefined) return null
  if (operator === 'gte') return value >= targetValue
  if (operator === 'lte') return value <= targetValue
  if (operator === 'range' && targetMin !== undefined && targetMax !== undefined) {
    return value >= targetMin && value <= targetMax
  }
  return null
}

export function MetricChart({ data, unit, targetValue, targetMin, targetMax, operator }: MetricChartProps) {
  // Filter to actual data points (non-null, non-forecast)
  const actualData = data.filter((d): d is ActualDataPoint => d.value !== null && !d.isForecast)

  if (actualData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
        <p className="text-gray-500">No data available</p>
      </div>
    )
  }

  // Calculate forecast value for current period if needed
  const forecastValue = calculateForecastValue(actualData)
  const hasForecast = data.some(d => d.isForecast) && forecastValue !== null

  // Build chart data with separate values for actual vs forecast
  const chartData = data.map((d, idx) => {
    const isLastActual = idx === data.length - 2 && hasForecast
    if (d.isForecast && forecastValue !== null) {
      // Forecast point: only forecastValue, no actualValue
      return { ...d, value: forecastValue, actualValue: null, forecastValue }
    }
    // Actual point: set actualValue, and forecastValue only for last actual (to connect)
    return {
      ...d,
      actualValue: d.value,
      forecastValue: isLastActual ? d.value : null,
    }
  })

  // Calculate Y-axis domain with padding
  const values = actualData.map(d => d.value)
  const allValues = [...values]
  if (forecastValue !== null) allValues.push(forecastValue)
  if (targetValue !== undefined) allValues.push(targetValue)
  if (targetMin !== undefined) allValues.push(targetMin)
  if (targetMax !== undefined) allValues.push(targetMax)

  const minVal = Math.min(...allValues)
  const maxVal = Math.max(...allValues)
  const padding = (maxVal - minVal) * 0.1 || 10
  const yDomain: [number, number] = [Math.max(0, minVal - padding), maxVal + padding]

  // Determine line color based on latest actual value vs target
  const latestActual = actualData[actualData.length - 1]
  const latestValue = latestActual?.value
  let lineColor = '#3EB1C8' // brand-blue default
  if (targetValue !== undefined && latestValue !== undefined) {
    if (operator === 'gte') {
      lineColor = latestValue >= targetValue ? '#249E6B' : '#c45c4d'
    } else if (operator === 'lte') {
      lineColor = latestValue <= targetValue ? '#249E6B' : '#c45c4d'
    } else if (operator === 'range' && targetMin !== undefined && targetMax !== undefined) {
      lineColor = latestValue >= targetMin && latestValue <= targetMax ? '#249E6B' : '#c45c4d'
    }
  }

  // Custom dot renderer - different style for forecast point
  const renderDot = (props: { cx?: number; cy?: number; payload?: DataPoint }) => {
    const { cx, cy, payload } = props
    if (!cx || !cy || !payload || payload.value === null) return <></>

    if (payload.isForecast) {
      // Gray hollow dot for forecast
      return <circle cx={cx} cy={cy} r={4} fill="#fff" stroke="#9ca3af" strokeWidth={2} />
    }

    // Colored dot for actual data based on target status
    const isMet = isValueMet(payload.value, operator, targetValue, targetMin, targetMax)
    const fill = isMet === null ? '#3EB1C8' : isMet ? '#249E6B' : '#c45c4d'
    return <circle cx={cx} cy={cy} r={4} fill={fill} />
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickLine={{ stroke: '#e5e7eb' }}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            domain={yDomain}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickLine={{ stroke: '#e5e7eb' }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickFormatter={(v) => formatValue(v, unit)}
            width={60}
          />
          <Tooltip content={<CustomTooltip unit={unit} operator={operator} targetValue={targetValue} targetMin={targetMin} targetMax={targetMax} />} />

          {/* Target reference lines */}
          {operator === 'range' && targetMin !== undefined && targetMax !== undefined ? (
            <>
              <ReferenceLine y={targetMin} stroke="#9ca3af" strokeDasharray="5 5" label={{ value: 'Min', position: 'right', fontSize: 10, fill: '#9ca3af' }} />
              <ReferenceLine y={targetMax} stroke="#9ca3af" strokeDasharray="5 5" label={{ value: 'Max', position: 'right', fontSize: 10, fill: '#9ca3af' }} />
            </>
          ) : targetValue !== undefined && (
            <ReferenceLine
              y={targetValue}
              stroke="#9ca3af"
              strokeDasharray="5 5"
              label={{ value: `Target: ${formatValue(targetValue, unit)}`, position: 'right', fontSize: 10, fill: '#9ca3af' }}
            />
          )}

          {/* Solid line for actual data only (stops at last actual point) */}
          <Line
            type="monotone"
            dataKey="actualValue"
            stroke={lineColor}
            strokeWidth={2}
            dot={renderDot}
            activeDot={{ r: 6, stroke: lineColor, strokeWidth: 2, fill: '#fff' }}
            connectNulls={false}
            animationDuration={1000}
          />

          {/* Dashed gray line for forecast segment (last actual → forecast) */}
          {hasForecast && (
            <Line
              type="monotone"
              dataKey="forecastValue"
              stroke="#9ca3af"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={(props) => {
                // Only show dot on forecast point
                if (props.payload?.isForecast) {
                  return <circle cx={props.cx} cy={props.cy} r={4} fill="#fff" stroke="#9ca3af" strokeWidth={2} />
                }
                return <></>
              }}
              activeDot={false}
              connectNulls
              animationDuration={1000}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
